import React, { Suspense, useEffect } from 'react';
import { RouterProvider, createBrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { createConfiguredRouter } from './router-config';
import './App.css';
import './i18n';
import { useTranslation } from 'react-i18next';
import { captureAttribution } from './utils/attribution';
import { getDeviceFingerprint } from './utils/deviceFingerprint';

// Импорт контекста
import { AuthProvider, useAuth } from './context/AuthContext';
import { ReportStateProvider } from './context/ReportStateContext';

// Импорт страниц
import HomePage from './pages/HomePage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ReportsPage from './pages/ReportsPage';
import CreateReportPage from './pages/CreateReportPage';
import StepReportPage from './pages/StepReportPage';
import ProfilePage from './pages/ProfilePage';
import AffiliatePage from './pages/AffiliatePage';
import TicksPage from './pages/TicksPage';
import PaymentPage from './pages/PaymentPage';
import UserReportsPage from './pages/UserReportsPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import EmailVerifyPage from './pages/EmailVerifyPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import UnsubscribePage from './pages/UnsubscribePage';
import TemplatesPage from './pages/TemplatesPage';
import TemplatePage from './pages/TemplatePage';

// Импорт компонентов
import Sidebar from './components/Sidebar';
import TopNavBar from './components/TopNavBar';
import StaticHeader from './components/StaticHeader';
import Footer from './components/Footer';

// Lazy-loaded heavy routes — keep these out of the main bundle:
// - EditorPage / DocumentsPage drag in @fortune-sheet/* (~3.4 MB) and its
//   formula-engine deps (formulajs, jstat, formula-parser)
// - ReportViewPage drags in chart.js + report rendering components
const EditorPage = React.lazy(() => import('./pages/EditorPage'));
const DocumentsPage = React.lazy(() => import('./pages/DocumentsPage'));
const ReportViewPage = React.lazy(() => import('./pages/ReportViewPage'));

// Lazy-loaded admin pages
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const AdminUsersPage = React.lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminReportsPage = React.lazy(() => import('./pages/admin/AdminReportsPage'));
const AdminTokensPage = React.lazy(() => import('./pages/admin/AdminTokensPage'));
const AdminNewsletterPage = React.lazy(() => import('./pages/admin/AdminNewsletterPage'));
const AdminChainsPage = React.lazy(() => import('./pages/admin/AdminChainsPage'));
const AdminPaymentsPage = React.lazy(() => import('./pages/admin/AdminPaymentsPage'));
const AdminPromoPage = React.lazy(() => import('./pages/admin/AdminPromoPage'));
const AdminSystemPage = React.lazy(() => import('./pages/admin/AdminSystemPage'));
const AdminEmailStatsPage = React.lazy(() => import('./pages/admin/AdminEmailStatsPage'));
const AdminExcelPage = React.lazy(() => import('./pages/admin/AdminExcelPage'));
const AdminUserActivityPage = React.lazy(() => import('./pages/admin/AdminUserActivityPage'));
const AdminRegistrationStatsPage = React.lazy(() => import('./pages/admin/AdminRegistrationStatsPage'));
const AdminReferralsPage = React.lazy(() => import('./pages/admin/AdminReferralsPage'));
const AdminReferralsDetailPage = React.lazy(() => import('./pages/admin/AdminReferralsDetailPage'));

const LoadingFallback: React.FC = () => {
  const { t } = useTranslation('common');
  return <div className="loading">{t('loading')}</div>;
};

// Компонент для защищенных маршрутов
interface ProtectedRouteProps {
  element: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element, requireAdmin = false }) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" />;
  }
  
  return <>{element}</>;
};

// Define routes for the router
const createAppRoutes = () => {
  // Helper function to create a protected route
  const createProtectedRoute = (element: React.ReactNode, requireAdmin = false) => {
    return {
      element: <ProtectedRoute element={element} requireAdmin={requireAdmin} />
    };
  };

  return [
    {
      path: '/',
      element: (
        <AuthProvider>
          <ReportStateProvider>
            <AppLayout />
          </ReportStateProvider>
        </AuthProvider>
      ),
      children: [
        { index: true, element: <LandingPage /> },
        { path: 'home', element: <HomePage /> },
        { 
          path: 'login', 
          element: <LoginWrapper /> 
        },
        { path: 'auth/callback', element: <AuthCallbackPage /> },
        { path: 'auth/token-received', element: <AuthCallbackPage /> },
        { path: 'auth/email/verify/:token', element: <EmailVerifyPage /> },
        { path: 'login/google/authorized', element: <AuthCallbackPage /> },
        { path: 'login/yandex/authorized', element: <AuthCallbackPage /> },
        { path: 'terms', element: <TermsPage /> },
        { path: 'privacy', element: <PrivacyPage /> },
        { path: 'legals', element: <Navigate to="/terms" replace /> },
        { path: 'contact', element: <ContactPage /> },
        { path: 'unsubscribe/:status', element: <UnsubscribePage /> },
        { 
          path: 'reports',
          ...createProtectedRoute(<ReportsPage />)
        },
        { 
          path: 'reports/create',
          ...createProtectedRoute(<CreateReportPage />)
        },
        {
          path: 'create-report',
          ...createProtectedRoute(<CreateReportPage />)
        },
        {
          path: 'create-report/:step',
          ...createProtectedRoute(<StepReportPage />)
        },
        {
          path: 'reports/:id',
          ...createProtectedRoute(<Suspense fallback={<LoadingFallback />}><ReportViewPage /></Suspense>)
        },
        {
          path: 'profile',
          ...createProtectedRoute(<ProfilePage />)
        },
        {
          path: 'affiliate',
          ...createProtectedRoute(<AffiliatePage />)
        },
        { 
          path: 'ticks',
          ...createProtectedRoute(<TicksPage />)
        },
        { 
          path: 'payment',
          ...createProtectedRoute(<PaymentPage />)
        },
        {
          path: 'payment/success',
          ...createProtectedRoute(<PaymentPage />)
        },
        { path: 'templates', element: <TemplatesPage /> },
        { path: 'templates/:slug', element: <TemplatePage /> },
        { path: 'editor', element: <Suspense fallback={<LoadingFallback />}><EditorPage /></Suspense> },
        { path: 'editor/:id', ...createProtectedRoute(<Suspense fallback={<LoadingFallback />}><EditorPage /></Suspense>) },
        { path: 'documents', ...createProtectedRoute(<Suspense fallback={<LoadingFallback />}><DocumentsPage /></Suspense>) },
        {
          path: 'admin',
          element: (
            <ProtectedRoute element={
              <Suspense fallback={<LoadingFallback />}>
                <AdminLayout />
              </Suspense>
            } requireAdmin={true} />
          ),
          children: [
            { index: true, element: <Navigate to="users" replace /> },
            { path: 'users', element: <Suspense fallback={<LoadingFallback />}><AdminUsersPage /></Suspense> },
            { path: 'users/:userId/reports', element: <Suspense fallback={<LoadingFallback />}><UserReportsPage /></Suspense> },
            { path: 'users/:userId/activity', element: <Suspense fallback={<LoadingFallback />}><AdminUserActivityPage /></Suspense> },
            { path: 'reports', element: <Suspense fallback={<LoadingFallback />}><AdminReportsPage /></Suspense> },
            { path: 'excel', element: <Suspense fallback={<LoadingFallback />}><AdminExcelPage /></Suspense> },
            { path: 'tokens', element: <Suspense fallback={<LoadingFallback />}><AdminTokensPage /></Suspense> },
            { path: 'payments', element: <Suspense fallback={<LoadingFallback />}><AdminPaymentsPage /></Suspense> },
            { path: 'promo', element: <Suspense fallback={<LoadingFallback />}><AdminPromoPage /></Suspense> },
            { path: 'newsletter', element: <Suspense fallback={<LoadingFallback />}><AdminNewsletterPage /></Suspense> },
            { path: 'chains', element: <Suspense fallback={<LoadingFallback />}><AdminChainsPage /></Suspense> },
            { path: 'email-stats', element: <Suspense fallback={<LoadingFallback />}><AdminEmailStatsPage /></Suspense> },
            { path: 'registration-stats', element: <Suspense fallback={<LoadingFallback />}><AdminRegistrationStatsPage /></Suspense> },
            { path: 'referrals', element: <Suspense fallback={<LoadingFallback />}><AdminReferralsPage /></Suspense> },
            { path: 'referrals/:partnerId', element: <Suspense fallback={<LoadingFallback />}><AdminReferralsDetailPage /></Suspense> },
            { path: 'system', element: <Suspense fallback={<LoadingFallback />}><AdminSystemPage /></Suspense> },
          ]
        },
        // Test routes
        { 
          path: 'test-report-events',
          element: (
            <div>
              <iframe 
                src="/api/test-report-events" 
                style={{ width: '100%', height: 'calc(100vh - 60px)', border: 'none' }}
                title="Test Report Events"
              />
            </div>
          )
        },
        { 
          path: 'test-create-report',
          element: (
            <div>
              <iframe 
                src="/api/test-create-report" 
                style={{ width: '100%', height: 'calc(100vh - 60px)', border: 'none' }}
                title="Test Create Report"
              />
            </div>
          )
        },
        { 
          path: 'test-report-status',
          element: (
            <div>
              <iframe 
                src="/api/test/test-report-status" 
                style={{ width: '100%', height: 'calc(100vh - 60px)', border: 'none' }}
                title="Test Report Status"
              />
            </div>
          )
        }
      ]
    }
  ];
};

// Wrapper component for login page with redirect logic
const LoginWrapper: React.FC = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Navigate to="/reports" /> : <LoginPage />;
};

// Layout component that handles header and footer
const AppLayout: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const isLandingPage = location.pathname === '/';
  const isStaticPage = ['/terms', '/privacy', '/contact', '/legals'].includes(location.pathname) || location.pathname.startsWith('/unsubscribe');
  const isTemplatesPage = location.pathname.startsWith('/templates');
  const isAuthPage = location.pathname === '/login' || location.pathname.startsWith('/auth/');
  const isCreateReportPage = location.pathname.startsWith('/create-report') || location.pathname === '/reports/create';
  const isEditorPage = location.pathname.startsWith('/editor');
  const isAdminPage = location.pathname.startsWith('/admin');

  // Three layout modes:
  // 1. TopNavBar: authenticated user pages (reports, profile, ticks, payment, report/:id)
  // 2. Sidebar: admin pages only
  // 3. Neither: landing, auth, static, create-report
  const showTopNavBar = isAuthenticated && !isLandingPage && !isAuthPage && !isStaticPage && !isCreateReportPage && !isAdminPage;
  const showSidebar = isAdminPage;
  const showStaticHeader = isStaticPage || (isTemplatesPage && !isAuthenticated);
  const showFooter = !isAuthPage && !isLandingPage && (isStaticPage || isTemplatesPage);

  const getMainClassName = () => {
    if (isLandingPage) {
      return "";
    } else if (isAuthPage || isEditorPage) {
      return "main-content no-padding";
    } else if (showSidebar) {
      return "main-content with-sidebar";
    } else if (showTopNavBar && isEditorPage) {
      return "main-content with-topnav no-padding";
    } else if (showTopNavBar) {
      return "main-content with-topnav";
    } else {
      return "main-content";
    }
  };

  const getAppClassName = () => {
    const editorClass = isEditorPage ? ' app-editor' : '';
    if (showTopNavBar) {
      return "app app-topnav" + editorClass;
    } else if (isLandingPage || isStaticPage) {
      return "app app-column" + editorClass;
    } else if (showSidebar) {
      return "app" + editorClass;
    } else {
      return "app app-column" + editorClass;
    }
  };

  return (
    <div className={getAppClassName()}>
      {showSidebar && <Sidebar />}
      {showTopNavBar && <TopNavBar />}
      {showStaticHeader && <StaticHeader />}
      <main className={getMainClassName()}>
        <Outlet />
      </main>
      {showFooter && <Footer />}
    </div>
  );
};

const App: React.FC = () => {
  const { t, i18n } = useTranslation('common');

  // Capture attribution (UTM params, referrer) on app mount,
  // and warm the device fingerprint cache so OAuth links can read it synchronously.
  useEffect(() => {
    captureAttribution();
    getDeviceFingerprint().catch(() => { /* never throw from FP capture */ });
  }, []);

  React.useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.title = t('app_title');
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', t('app_description'));
  }, [t, i18n.language]);

  // Create router with future flags enabled
  const router = createConfiguredRouter(createAppRoutes());

  return <RouterProvider router={router} />;
};

export default App;
